import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDrawerContext } from "../../context/drawerContext";
import BandCard from "../../components/BandCard";
import Button from "../../components/Button";
import { format } from "date-fns";
import Tick from '/public/images/icons/greenTick.svg?react';
import Cross from '/public/images/icons/redCross.svg?react';
import CloseIcon from '/public/images/icons/close.svg?react';
import ExclamationMark from '/public/images/icons/adhocCriteria.svg?react';
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function NormalScan() {
  const { user, selectedZone, selectedVenue, logout, updateTokens } = useUserContext();
  const { setTitle, setBackButton, setBackButtonFunc } = useHeaderContext();
  const { open, setOpen, setDrawerContent } = useDrawerContext();
    const location = useLocation();
    const state = location.state || {};
    const { band } = state;
    const navigate = useNavigate();

    const [zoneRequirements, setZoneRequirements] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [checkedIn, setCheckedIn] = useState(false);
    const [allHardStopsMet, setAllHardStopsMet] = useState(false);
    const [error, setError] = useState("");
    const [milestoneRequirements, setMilestoneRequirements] = useState(false);
    const [timeoutId, setTimeoutId] = useState(null);

    useEffect(() => {
        setTitle("SCANNED BAND");
        setBackButton(true);
        setBackButtonFunc(() => navigate('/scan?type=normal'));
        fetchZoneRequirements();
        fetchMilestoneRequirements();

        if (band?.BandStateVerbose === 'CHECKED IN') {
            setCheckedIn(true);
        }
    }, []);

    const fetchMilestoneRequirements = async () => {
        await Sentry.startSpan({ name: "Check Milestones", op: "http.request" }, async (span) => {
            try {
                const url = `${import.meta.env.VITE_API_URL}Milestone/CanParticipantBeAwardedMilestonesForZoneCheckOut?participantId=${band?.ParticipantId}&zoneId=${band?.CurrentZone || selectedZone?.ZoneID}`;
                span.setAttribute("http.url", url);
                span.setAttribute("participantId", band?.ParticipantId);
                span.setAttribute("zoneId", band?.CurrentZone);
          
                const response = await fetchWithRefresh(
                  url,
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: "Bearer " + user.token,
                    },
                  }, logout, updateTokens, (message) => { toast.info(message);}
                );
          
                if (!response.ok) {
                  const errorText = await response.text();
                  LogErrors(user, logout, updateTokens, `Failed to check milestone. Status: ${response.status}, Response: ${errorText}`);
                  span.setStatus("error");
                  throw new Error(`Failed to check milestone: ${errorText}`);
                }
          
                const milestoneResponse = await response.json();
                setMilestoneRequirements(milestoneResponse);
                span.setStatus("ok");
            } catch (error) {
                Sentry.captureException(error, { extra: { participantId: band?.ParticipantId, zoneId: band?.CurrentZone } });
                LogErrors(user, logout, updateTokens, "Error checking milestones: " + error);
                span.setStatus("error");
                throw error;
            } finally {
                span.end();
            }
        });
    }

    const fetchZoneRequirements = async () => {
      await Sentry.startSpan({ name: "Fetch Zone Requirements", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Zone/FetchZoneEntryCriteria?ZoneID=${selectedZone.ZoneID}&ParticipantId=${band.ParticipantId}`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            const data = await response.json();
            setZoneRequirements(data);
    
            const hardStopsMet = data.zoneMilestoneCriteria.every(req => req.participantHasMilestone === 1);
            setAllHardStopsMet(hardStopsMet);
            setIsButtonDisabled(data.hardStop === 1 && !hardStopsMet);
          } else {
            throw new Error("Failed to fetch zone requirements.");
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { ZoneID: selectedZone.ZoneID, ParticipantId: band.ParticipantId } });
          LogErrors(user, logout, updateTokens, "Couldn't reach zone requirements endpoint.");
        } finally {
          span.end();
        }
      });
    };
    
    const checkInUser = async () => {
      if (isButtonDisabled) return;
    
      await Sentry.startSpan({ name: "Check In User", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Bands/CheckIn?bandId=${band.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${selectedZone.ZoneID}`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setCheckedIn(true);
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    CHECKED IN
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => {navigate('/'), setOpen(false)}} />
                </div>
                <div className="mt-6 flex flex-col gap-6 justify-stretch">
                  <h3>Checked Into {selectedZone?.ZoneName} Successfully!</h3>
                  <Button onClick={closeModal} className="w-full">Close</Button>
                </div>
              </div>
            );
          } else {
            let errorMsg;
            if (response.status === 504 || response.status === 408) {
              errorMsg = "The band could not be checked in due to network connectivity issues. Please check your internet connection and retry.";
            } else {
              const contentType = response.headers.get("content-type");
              errorMsg = contentType && contentType.includes("application/json") ? await response.json() : await response.text();
            }
            setError(errorMsg);
            throw new Error(errorMsg);
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: band.BandID, UserID: user.id } });
          setError(error.message);
        } finally {
          span.end();
        }
      });
    };
    
    const checkOutUser = async () => {
      await Sentry.startSpan({ name: "Check Out User", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Bands/CheckOut?bandId=${band.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${band.CurrentZone}`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setCheckedIn(true);
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    CHECKED OUT
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={closeModal} />
                </div>
                <div className="mt-6 flex flex-col gap-6 justify-stretch">
                  <h3>Checked Out of {band?.ZoneName} Successfully!</h3>
                  <Button onClick={closeModal} className="w-full">Close</Button>
                </div>
              </div>
            );

            // Set timeout to close modal
            const timeoutId = setTimeout(() => {
              closeModal();
            }, 2000);
            setTimeoutId(timeoutId); // Save timeoutId to state
          } else {
            throw new Error("Failed to check band out of zone.");
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: band.BandID, UserID: user.id } });
          LogErrors(user, logout, updateTokens, "Couldn't check out.");
        } finally {
          span.end();
        }
      });
    };
    
    const checkOutUserNoActivity = async () => {
      await Sentry.startSpan({ name: "Check Out User (No Activity)", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Bands/CheckOutNoActivity?bandId=${band.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${band.CurrentZone}`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setCheckedIn(true);
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    CHECKED OUT
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={closeModal} />
                </div>
                <div className="mt-6 flex flex-col gap-6 justify-stretch">
                  <h3>Checked Out of {band?.ZoneName} Successfully!</h3>
                  <Button onClick={closeModal} className="w-full">Close</Button>
                </div>
              </div>
            );
    
             // Set timeout to close modal
             const timeoutId = setTimeout(() => {
              closeModal();
            }, 2000);
            setTimeoutId(timeoutId); // Save timeoutId to state
          } else {
            throw new Error(`Failed to check band out of zone. Status: ${response.status} ${response.statusText}`);
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: band.BandID, UserID: user.id } });
          LogErrors(user, logout, updateTokens, "Couldn't reach checkout endpoint: " + error);
        } finally {
          span.end();
        }
      });
    };

    useEffect(() => {
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId); // Clear timeout on unmount
        }
      };
    }, [timeoutId]);

    const closeModal = () => {
        navigate('/scan?type=normal');
    };

    const deactivateAndCheckOut = async () => {
      await Sentry.startSpan({ name: "Unassign & Check Out", op: "http.request" }, async (span) => {
        try {
          const checkoutUrl = `${import.meta.env.VITE_API_URL}Bands/CheckOutNoActivity?bandId=${band.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${band.CurrentZone}`;
          const checkoutResponse = await fetchWithRefresh(checkoutUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });

          if (!checkoutResponse.ok) {
            throw new Error(`Failed to check band out of zone. Status: ${checkoutResponse.status} ${checkoutResponse.statusText}`);
          }

          const bookoutUrl = `${import.meta.env.VITE_API_URL}scan/bookout?bookingParticipantId=${band?.BookingParticipantId}&scanId=${band?.BandID}&bookingId=${band?.BookingId}`;
          span.setAttribute("http.url", bookoutUrl);

          const bookoutResponse = await fetchWithRefresh(bookoutUrl, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });

          if (!bookoutResponse.ok) {
            const contentType = bookoutResponse.headers.get("content-type");
            const errorMsg = contentType && contentType.includes("application/json") ? await bookoutResponse.json() : await bookoutResponse.text();
            setError(errorMsg);
            throw new Error(errorMsg);
          }

          setOpen(true);
          setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">Booked Out</h2>
                <CloseIcon className="cursor-pointer" onClick={closeModal} />
              </div>
              <div className="mt-6 flex flex-col gap-6 justify-stretch">
                <h3>Band Booked Out & Checked Out Successfully!</h3>
                <Button onClick={closeModal} className="w-full">Close</Button>
              </div>
            </div>
          );
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: band?.BandID, BookingParticipantId: band?.BookingParticipantId } });
          LogErrors(user, logout, updateTokens, "Couldn't complete unassign & check out: " + error);
        } finally {
          span.end();
        }
      });
    };

    const formattedLastUpdated = band?.LastUpdated ? format(new Date(band.LastUpdated), 'dd/MM/yyyy') : 'Unknown Date';

    const formatBookingTime = (time) => (typeof time === "string" && time.length >= 5 ? time.substring(0, 5) : null);

    const bookingDetails = band?.BookingId
      ? {
        thingName: band?.BookableThingName,
        date: band?.BookingDate ? format(new Date(band.BookingDate), "dd/MM") : null,
        startTime: formatBookingTime(band?.BookingStartTime),
        endTime: formatBookingTime(band?.BookingEndTime),
      }
      : null;

    const bookingTimeLabel = bookingDetails?.startTime && bookingDetails?.endTime
      ? `${bookingDetails.startTime} - ${bookingDetails.endTime}`
      : bookingDetails?.startTime
        ? `From ${bookingDetails.startTime}`
        : bookingDetails?.endTime
          ? `Until ${bookingDetails.endTime}`
          : null;

    const capitalizeFirstLetter = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Filter out duplicates in zoneMilestoneCriteria based on zoneEntryCriteriaId
    const uniqueZoneMilestoneCriteria = zoneRequirements?.zoneMilestoneCriteria?.reduce((acc, current) => {
        const x = acc.find(item => item.zoneEntryCriteriaId === current.zoneEntryCriteriaId);
        if (!x) {
            return acc.concat([current]);
        } else {
            return acc;
        }
    }, []);

    const matchingZone = selectedVenue?.zones?.find(zone => zone.ZoneID === band?.CurrentZone);

    if(!milestoneRequirements){
        return (
            <div className="flex flex-col justify-center items-center h-full gap-4">
                <div className="loading loading-spinner loading-lg">Loading...</div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-[30px]">
            <BandCard band={band} size="sm" participant={band} />

            <div>
                <h3>{band?.ParticipantName}</h3>
                <p className="text-sportsGrey"><span className="capitalize">{capitalizeFirstLetter(band?.BandStateVerbose || '')}</span> • Last Updated • {formattedLastUpdated}</p>
                {bookingDetails && (
                  <div className="mt-2 rounded-lg border border-[#E3E6EA] bg-[#F8FAFC] px-3 py-2">
                    <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-sportsBlack">Booking</p>
                    <p className="font-semibold font-poppins text-sportsBlack leading-tight mt-0.5">
                      {bookingDetails.thingName || "Booked Activity"}
                    </p>
                    <p className="font-poppins text-[13px] font-medium text-sportsBlack mt-1">
                      {bookingTimeLabel || "Time unavailable"}
                      {bookingDetails.date && ` • ${bookingDetails.date}`}
                    </p>
                  </div>
                )}
            </div>

            <div className="dashed-border w-full h-0.5" />
            {(!checkedIn || band?.CurrentZone !== selectedZone?.ZoneID) && (
                <>
                    {(zoneRequirements?.zoneAdHocCriteria?.length > 0 || uniqueZoneMilestoneCriteria?.length > 0 || uniqueZoneMilestoneCriteria?.length > 0 || zoneRequirements?.zoneAdHocCriteria?.length > 0 ) && (
                        <div>
                            {(zoneRequirements?.zoneAdHocCriteria?.length > 0 || uniqueZoneMilestoneCriteria?.length > 0) && (
                                <>
                                    <p className="font-semibold font-poppins">{zoneRequirements.hardStop === 1 ? 'Hard Stop' : 'Soft Stop' }</p>
                                    <p className="font-poppins mb-[20px]">
                                        {allHardStopsMet 
                                            ? "Participant meets criteria" 
                                            : zoneRequirements.hardStop === 1 
                                                ? "Can't check participant in, see below:" 
                                                : "Criteria needs review, see below:"
                                        }
                                    </p>
                                </>
                            )}
                            {uniqueZoneMilestoneCriteria?.length > 0 && (
                                <ul className="">
                                    {uniqueZoneMilestoneCriteria.map((requirement, index) => (
                                        <div key={index} className="flex flex-row align-middle items-center gap-x-3 mb-[4px]">
                                            {requirement.participantHasMilestone === 1 ? <Tick /> : <Cross />}
                                            <li className={`${requirement.participantHasMilestone === 1 ? 'text-[#47B405]' : 'text-[#DF3327]'}`}>{requirement.milestoneName}</li>
                                        </div>
                                    ))}
                                </ul>
                            )}
                            {zoneRequirements?.zoneAdHocCriteria?.length > 0 && (
                                <ul className="">
                                    {zoneRequirements?.zoneAdHocCriteria.map((requirement, index) => (
                                        <div key={requirement?.zoneEntryCriteriaId} className="flex flex-row align-middle items-center gap-x-3 mb-[4px]">
                                            <ExclamationMark />
                                            <li>{requirement.criteriaDescription}</li>
                                        </div>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </>
            )}
            {error && (<p className="text-red-500">{error}</p>)}
            {!open && (
              <div className="flex flex-col gap-6">
                {!checkedIn ? (
                  <>
                    <Button className={`w-full ${isButtonDisabled ? 'opacity-50 cursor-auto' : ''}`} onClick={isButtonDisabled ? undefined : checkInUser}>
                      Check Into {selectedZone?.ZoneName}
                    </Button>
                  </>
                ) : (
                  <>
                    {band?.CurrentZone !== selectedZone?.ZoneID && (
                      <Button className={`w-full ${isButtonDisabled ? 'opacity-50 cursor-auto' : ''}`} onClick={isButtonDisabled ? undefined : checkInUser}>Check Into {selectedZone?.ZoneName}</Button>
                    )}

                    {milestoneRequirements?.MilestonesAvailable === 1 && !band?.IsGuestBand && !band?.isSingleUse && (
                      <Button className={`w-full`} onClick={checkOutUser}>Award Milestone and Check Out</Button>
                    )}

                    <Button className={`w-full`} onClick={checkOutUserNoActivity}>Check Out {band?.ZoneName}</Button>

                    {band?.BookingId && (
                      <Button className="w-full !bg-[#A6093D] text-white" onClick={deactivateAndCheckOut}>Book Out &amp; Check Out</Button>
                    )}
                  </>
                )}
                <Button variant="quaternary" className="w-full" onClick={() => closeModal()}>Cancel</Button>
              </div>
            )}

        </div>
    );
}